// Classification caractère-par-caractère puis regroupement en runs.
// Chaque caractère est soit "math" (chiffre, opérateur, ponctuation
// mathématique), soit "text" (lettre + accents + underscore). Les
// espaces et la virgule décimale héritent du contexte voisin.
// Les runs "text" sont enveloppés dans \text{...} (préserve les espaces) ;
// les runs "math" subissent une substitution de symboles spéciaux.

const LETTER_RE = /[a-zàâäéèêëïîôöùûüÿç]/i;
const DIGIT_RE = /[0-9]/;
// Symboles unaires/binaires considérés comme math purs
const MATH_OP = new Set(["+", "-", "−", "=", "<", ">", "*", "/", "|", "·", "≤", "≥", "²", "×", "÷", "%", "^"]);

const escapeForText = (s) =>
  s
    .replace(/\\/g, "\\textbackslash{}")
    .replace(/_/g, "\\_")
    .replace(/\$/g, "\\$")
    .replace(/&/g, "\\&")
    .replace(/#/g, "\\#")
    .replace(/\{/g, "\\{")
    .replace(/\}/g, "\\}");

const transformMathChar = (ch) => {
  switch (ch) {
    case "×":
      return " \\times ";
    case "÷":
      return " \\div ";
    case "−":
      return " - ";
    case "²":
      return "^{2}";
    case "%":
      return "\\%";
    default:
      return ch;
  }
};

const classify = (ch) => {
  if (ch === "_" || LETTER_RE.test(ch)) return "text";
  if (DIGIT_RE.test(ch) || MATH_OP.has(ch) || ch === "(" || ch === ")") return "math";
  return "neutral"; // espace, virgule, point, etc.
};

export const formulaToLatex = (formula) => {
  if (!formula || typeof formula !== "string") return null;

  try {
    const n = formula.length;
    if (n === 0) return null;

    // 1. Étiquette chaque caractère : "text", "math" ou "neutral".
    const tags = new Array(n);
    for (let i = 0; i < n; i++) {
      tags[i] = classify(formula[i]);
    }

    // 2. Les neutres héritent : si entourés de "text" des deux côtés
    //    (en sautant d'autres neutres), ils deviennent "text". Pareil
    //    pour math. Cas isolé → text par défaut (on préfère que la
    //    ponctuation isolée colle à un mot voisin).
    const resolveNeutral = (i) => {
      // cherche le voisin non-neutre à gauche
      let left = null;
      for (let j = i - 1; j >= 0; j--) {
        if (tags[j] !== "neutral") {
          left = tags[j];
          break;
        }
      }
      let right = null;
      for (let j = i + 1; j < n; j++) {
        if (tags[j] !== "neutral") {
          right = tags[j];
          break;
        }
      }
      // Règle : si au moins un côté est "text", neutral → text.
      // Sinon si au moins un côté est "math", neutral → math.
      // Sinon (formule entièrement neutre, improbable) → text.
      if (left === "text" || right === "text") return "text";
      if (left === "math" || right === "math") return "math";
      return "text";
    };

    for (let i = 0; i < n; i++) {
      if (tags[i] === "neutral") {
        tags[i] = resolveNeutral(i);
      }
    }

    // 3. Regroupe en runs et émet la sortie.
    const parts = [];
    let i = 0;
    while (i < n) {
      const tag = tags[i];
      let j = i + 1;
      while (j < n && tags[j] === tag) j++;
      const segment = formula.slice(i, j);
      if (tag === "text") {
        parts.push(`\\text{${escapeForText(segment)}}`);
      } else {
        // math : transforme les symboles spéciaux char par char
        let out = "";
        for (const c of segment) out += transformMathChar(c);
        parts.push(out);
      }
      i = j;
    }

    const result = parts.join("").trim();
    return result || null;
  } catch (e) {
    return null;
  }
};
